import { useState, useMemo, useCallback } from "react";
// Mightier Survey Engine
import { SurveyEngineIface } from "./useSurveyEngine";
import { Step } from "@neuromotion-inc/mightier-survey-engine";

interface SurveyNavigation {
  currentStepIndex: number;
  currentStep: Step;
  isOnLastStep: boolean;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
}

export const useSurveyNavigationV2 = (
  surveyEngine: SurveyEngineIface,
): SurveyNavigation => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(() => {
    const firstUnansweredStepIndex =
      surveyEngine.stepsAndQuestionsToDisplay.findIndex((step: Step) => {
        const hasNotBeenSubmitted = !surveyEngine.hasStepBeenSubmitted(step.id);
        return hasNotBeenSubmitted;
      });

    // If all steps have been answered, return the last step's index
    if (firstUnansweredStepIndex === -1) {
      return surveyEngine.stepsAndQuestionsToDisplay.length - 1;
    }

    return firstUnansweredStepIndex;
  });

  const currentStep = useMemo(() => {
    return surveyEngine.getStepByIdx(currentStepIndex);
  }, [surveyEngine, currentStepIndex]);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < surveyEngine.stepsAndQuestionsToDisplay.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [surveyEngine, currentStepIndex]);

  const isOnLastStep = useMemo(() => {
    // NOTE: for some reason I just don't trust `currentStepIndex === arr.length - 1` works
    // in all browsers lol
    const lastStepIdx = surveyEngine.stepsAndQuestionsToDisplay.length - 1;
    return Math.round(currentStepIndex) === Math.round(lastStepIdx);
  }, [surveyEngine.stepsAndQuestionsToDisplay.length, currentStepIndex]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  return useMemo(
    () => ({
      currentStepIndex,
      currentStep,
      isOnLastStep,
      goToPreviousStep,
      goToNextStep,
    }),
    [
      currentStepIndex,
      currentStep,
      isOnLastStep,
      goToNextStep,
      goToPreviousStep,
    ],
  );
};
